// src/lib/stack-app/apps/implementations/event-tracker.ts
import { isBrowserLike } from "@stackframe/stack-shared/dist/utils/env";
import { runAsynchronously } from "@stackframe/stack-shared/dist/utils/promises";
import { generateUuid } from "./session-replay.js";
var FLUSH_INTERVAL_MS = 1e4;
var MAX_EVENTS_PER_BATCH = 50;
var MAX_APPROX_BYTES_PER_BATCH = 64e3;
var EventTracker = class {
  constructor(deps) {
    this._started = false;
    this._cancelled = false;
    this._detachListeners = null;
    this._flushTimer = null;
    this._events = [];
    this._approxBytes = 0;
    this._lastKnownAccessToken = null;
    this._lastUrl = null;
    this._originalPushState = null;
    this._originalReplaceState = null;
    this._onPopState = () => {
      this._capturePageView("pop");
    };
    this._onClickCapture = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      this._pushEvent({
        event_type: "$click",
        event_at_ms: Date.now(),
        data: {
          tag_name: target.tagName.toLowerCase(),
          text: target.textContent?.trim().substring(0, 200) ?? null,
          href: this._findNearestAnchorHref(target),
          selector: this._buildSelector(target),
          x: event.clientX,
          y: event.clientY,
          page_x: event.pageX,
          page_y: event.pageY,
          viewport_width: window.innerWidth,
          viewport_height: window.innerHeight
        }
      });
    };
    this._onPageHide = () => {
      runAsynchronously(() => this._flush({ keepalive: true }), { noErrorLogging: true });
    };
    this._deps = deps;
    this._sessionReplaySegmentId = generateUuid();
  }
  start() {
    if (this._started) return;
    if (!isBrowserLike()) return;
    this._started = true;
    this._setupPageViewCapture();
    this._setupClickCapture();
    this._setupPageHideListeners();
    this._flushTimer = setInterval(() => this._tick(), FLUSH_INTERVAL_MS);
  }
  stop() {
    this._cancelled = true;
    if (this._flushTimer !== null) {
      clearInterval(this._flushTimer);
      this._flushTimer = null;
    }
    runAsynchronously(() => this._flush({ keepalive: true }), { noErrorLogging: true });
    this._teardown();
  }
  clearBuffer() {
    this._events = [];
    this._approxBytes = 0;
  }
  _pushEvent(event) {
    this._events.push(event);
    this._approxBytes += JSON.stringify(event).length;
    if (this._events.length >= MAX_EVENTS_PER_BATCH || this._approxBytes >= MAX_APPROX_BYTES_PER_BATCH) {
      runAsynchronously(() => this._flush({ keepalive: false }), { noErrorLogging: true });
    }
  }
  _capturePageView(entryType) {
    const url = window.location.href;
    if (url === this._lastUrl && entryType !== "initial") return;
    this._lastUrl = url;
    this._pushEvent({
      event_type: "$page-view",
      event_at_ms: Date.now(),
      data: {
        url,
        path: window.location.pathname,
        referrer: document.referrer,
        title: document.title,
        entry_type: entryType,
        viewport_width: window.innerWidth,
        viewport_height: window.innerHeight,
        screen_width: window.screen.width,
        screen_height: window.screen.height
      }
    });
  }
  _setupPageViewCapture() {
    this._capturePageView("initial");
    this._originalPushState = history.pushState.bind(history);
    history.pushState = (...args) => {
      this._originalPushState(...args);
      this._capturePageView("push");
    };
    this._originalReplaceState = history.replaceState.bind(history);
    history.replaceState = (...args) => {
      this._originalReplaceState(...args);
      this._capturePageView("replace");
    };
    window.addEventListener("popstate", this._onPopState);
  }
  _buildSelector(element) {
    const parts = [];
    let current = element;
    let depth = 0;
    while (current && depth < 5) {
      let part = current.tagName.toLowerCase();
      if (current.id) {
        part += `#${current.id}`;
        parts.unshift(part);
        break;
      }
      if (current.className && typeof current.className === "string") {
        const classes = current.className.trim().split(/\s+/).filter(Boolean);
        if (classes.length > 0) {
          part += `.${classes.join(".")}`;
        }
      }
      parts.unshift(part);
      current = current.parentElement;
      depth++;
    }
    return parts.join(" > ");
  }
  _findNearestAnchorHref(element) {
    let current = element;
    while (current) {
      if (current.tagName === "A" && current.hasAttribute("href")) {
        return current.getAttribute("href");
      }
      current = current.parentElement;
    }
    return null;
  }
  _setupClickCapture() {
    document.addEventListener("click", this._onClickCapture, { capture: true });
  }
  _setupPageHideListeners() {
    window.addEventListener("pagehide", this._onPageHide);
    document.addEventListener("visibilitychange", this._onPageHide);
    this._detachListeners = () => {
      window.removeEventListener("pagehide", this._onPageHide);
      document.removeEventListener("visibilitychange", this._onPageHide);
    };
  }
  _teardown() {
    if (this._detachListeners) {
      this._detachListeners();
      this._detachListeners = null;
    }
    if (this._originalPushState) {
      history.pushState = this._originalPushState;
      this._originalPushState = null;
    }
    if (this._originalReplaceState) {
      history.replaceState = this._originalReplaceState;
      this._originalReplaceState = null;
    }
    window.removeEventListener("popstate", this._onPopState);
    document.removeEventListener("click", this._onClickCapture, { capture: true });
    this._events = [];
    this._approxBytes = 0;
  }
  async _flush(options) {
    if (!this._lastKnownAccessToken) return;
    if (this._events.length === 0) return;
    const nowMs = Date.now();
    const batchId = generateUuid();
    const payload = {
      session_replay_segment_id: this._sessionReplaySegmentId,
      batch_id: batchId,
      sent_at_ms: nowMs,
      events: this._events
    };
    this._events = [];
    this._approxBytes = 0;
    const res = await this._deps.sendBatch(
      JSON.stringify(payload),
      { keepalive: options.keepalive }
    );
    if (res.status === "error") {
      console.warn("EventTracker flush failed:", res.error);
      return;
    }
    if (!res.data.ok) {
      console.warn("EventTracker flush failed:", res.data.status, await res.data.text());
    }
  }
  _tick() {
    if (this._cancelled) return;
    runAsynchronously(async () => {
      this._lastKnownAccessToken = await this._deps.getAccessToken();
    }, { noErrorLogging: true });
    const hasAuth = !!this._lastKnownAccessToken;
    if (hasAuth && this._events.length > 0) {
      runAsynchronously(() => this._flush({ keepalive: false }), { noErrorLogging: true });
    }
  }
};
export {
  EventTracker
};
//# sourceMappingURL=event-tracker.js.map